import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Group, Texture } from "three";
import { WindowSize, GridSize } from "../../../../../../models/door_configuration";
import {
  MM,
  ALTEZZA_PANNELLO_M,
  OverbasStep,
  IntegratedDoorPosition,
  getBasculanteTransform,
} from "./constants";
import { getPortinaX, Portina } from "./portina_integrata";
import { PannelloSingolo } from "./pannello_singolo";
import { ManigliaLock, ManigliaSquare } from "./maniglia_basculante";

export type PannelliAnimatiProps = {
  larghezza: number;
  altezza: number;
  spessore: number;
  isOpen: boolean;
  showPortina: boolean;
  portinaPosizione: IntegratedDoorPosition;
  portinaLarghezza: number;
  portinaColore: Texture | undefined;
  portinaColoreInterno: Texture | undefined;
  portinaManigliaCOlore: string;
  portinaManigliaCOloreInterno: string;
  portinaSogliaAltezza: number;
  portinaApertura: "sx" | "dx";
  step: OverbasStep;
  finestreAttive: Set<string>;
  griglieAttive: Set<string>;
  onFinestraClick: (panelIndex: number, finestraIndex: number) => void;
  colorePannello: Texture | undefined;
  coloreInternoPannello: Texture | undefined;
  corniceFinestratura: Texture | undefined;
  isWide: boolean;
  isFullVision: boolean;
  isTwoPanels: boolean;
  pietraveraColumns: number;
  windowSize: WindowSize;
  hoverColor: string;
  gridSize: GridSize;
  grigliaColor: string | undefined;
  tipoManiglia?: string | null;
  coloreManiglia?: string;
  rawAltezza?: number;
  isDebordante?: boolean;
};

export const PannelliAnimati: React.FC<PannelliAnimatiProps> = ({
  larghezza,
  altezza,
  spessore,
  isOpen,
  showPortina,
  portinaPosizione,
  portinaLarghezza,
  portinaColore,
  portinaColoreInterno,
  portinaManigliaCOlore,
  portinaManigliaCOloreInterno,
  portinaSogliaAltezza,
  portinaApertura,
  step,
  finestreAttive,
  griglieAttive,
  onFinestraClick,
  colorePannello,
  coloreInternoPannello,
  corniceFinestratura,
  isWide,
  isFullVision,
  isTwoPanels,
  pietraveraColumns,
  windowSize,
  hoverColor,
  gridSize,
  grigliaColor,
  tipoManiglia,
  coloreManiglia,
  rawAltezza = 0,
  isDebordante = true,
}) => {
  const progressRef = useRef(0);
  const groupRef    = useRef<Group>(null);

  const panels = useMemo(() => {
    const list: { y: number; h: number; index: number }[] = [];
    if (isTwoPanels) {
      const h = altezza / 2;
      list.push({ y: h / 2,         h, index: 0 });
      list.push({ y: h + h / 2,     h, index: 1 });
    } else {
      let y = 0;
      let index = 0;
      while (y < altezza) {
        const h = Math.min(ALTEZZA_PANNELLO_M, altezza - y);
        list.push({ y: y + h / 2, h, index });
        y += h;
        index++;
      }
    }
    return list;
  }, [altezza, isTwoPanels]);

  const portinaX    = getPortinaX(portinaPosizione, larghezza, portinaLarghezza);
  const isDoorClosed = progressRef.current < 0.1;

  // Pannello immediatamente sopra il centro porta (per la serratura) e secondo
  // pannello dal basso (per il quadratino semplice) della doppia maniglia. Con
  // soli 2 pannelli (es. plana a fasce larghe) il "secondo dal basso" coincide
  // col pannello della serratura: in quel caso il quadratino scende sul primo
  // pannello, cosi' resta visibile sotto la serratura invece di sparirci dietro.
  const manigliaLockPanel       = panels.find(p => p.y > altezza / 2) ?? panels[panels.length - 1] ?? { y: altezza / 2, index: -1 };
  const manigliaSquareCandidate = panels[1] ?? panels[0] ?? { y: altezza / 2, index: -1 };
  const manigliaSquarePanel     = manigliaSquareCandidate.index === manigliaLockPanel.index
    ? (panels[0] ?? manigliaSquareCandidate)
    : manigliaSquareCandidate;
  const manigliaLockY   = manigliaLockPanel.y;
  const manigliaSquareY = manigliaSquarePanel.y;

  const isGlasspanel = windowSize === WindowSize.glasspanel ||
    windowSize === WindowSize.glasspanel_vetro_6_7 ||
    windowSize === WindowSize.glasspanel_vetro_5 ||
    windowSize === WindowSize.glasspanel_vetro_4_12_4;

  const glasspanelLimitReached = useMemo(() => {
    if (!isGlasspanel) return false;
    const eligibili = panels.filter(p => p.h > MM(200));
    if (eligibili.length !== 5) return false;
    const attivi = eligibili.filter(p => finestreAttive.has(`${p.index}-0`)).length;
    return attivi >= 4;
  }, [isGlasspanel, panels, finestreAttive]);

  useFrame(() => {
    const target = isOpen ? 1 : 0;
    progressRef.current = Math.max(
      0,
      Math.min(1, progressRef.current + (target - progressRef.current) * 0.002)
    );

    const group = groupRef.current;
    if (!group) return;

    const { cy, cz, rotX } = getBasculanteTransform(progressRef.current, altezza, isDebordante);
    group.position.set(larghezza / 2, cy, cz);
    group.rotation.x = rotX;
  });

  return (
    <group ref={groupRef}>
      {panels.map(({ y, h, index }) => (
        <group key={index} position={[0, y - altezza / 2, 0]}>
          <PannelloSingolo
            larghezza={larghezza}
            altezza={h}
            spessore={spessore}
            panelIndex={index}
            panelWorldY={y}
            showPortina={showPortina}
            portinaX={portinaX}
            portinaLarghezza={portinaLarghezza}
            step={step}
            finestreAttive={finestreAttive}
            griglieAttive={griglieAttive}
            onFinestraClick={onFinestraClick}
            isDoorClosed={isDoorClosed}
            colorePannello={colorePannello}
            coloreInternoPannello={coloreInternoPannello}
            corniceFinestratura={corniceFinestratura}
            isWide={isWide}
            isFullVision={isFullVision}
            isTwoPanels={isTwoPanels}
            pietraveraColumns={pietraveraColumns}
            windowSize={windowSize}
            glasspanelLimitReached={glasspanelLimitReached}
            hoverColor={hoverColor}
            gridSize={gridSize}
            grigliaColor={grigliaColor}
            tipoManiglia={tipoManiglia}
            manigliaLockY={manigliaLockY}
            manigliaSquareY={manigliaSquareY}
          />
        </group>
      ))}

      {showPortina && (
        <Portina
          spessore={spessore}
          altezza={altezza}
          portinaX={portinaX}
          portinaLarghezza={portinaLarghezza}
          colore={portinaColore}
          coloreInterno={portinaColoreInterno}
          manigliaColore={portinaManigliaCOlore}
          manigliaColoreInterno={portinaManigliaCOloreInterno}
          sogliaAltezza={portinaSogliaAltezza}
          apertura={portinaApertura}
        />
      )}

      <ManigliaLock
        y={manigliaLockY - altezza / 2}
        spessore={spessore}
        tipoManiglia={tipoManiglia ?? null}
        coloreManiglia={coloreManiglia}
      />
      <ManigliaSquare
        y={manigliaSquareY - altezza / 2}
        spessore={spessore}
        tipoManiglia={tipoManiglia ?? null}
        coloreManiglia={coloreManiglia}
      />
    </group>
  );
};
